import { useCMSPage } from '@web-app/hooks/use-cms-page';
import type { InferGetServerSidePropsType } from 'next/types';

import { ARTICLES_TO_SHOW } from '@core/constant';
import { formatAlternates, getSanityQuery } from '@core/sanity/utils';
import { ERouting, ERoutingDynamic } from '@core/type';
import { DynamicSEOEntity, BreadcrumbEntity } from '@core/type/api';
import { SanityBlogCategory } from '@core/type/sanity';

import { fetchSanityData } from '../../../helpers/make-server-props/helpers/sanity';
import {
  isRedirect,
  makeServerSideProps,
} from '../../../helpers/make-server-props/make-server-props';
import { ViewBlogCategory } from '../../../view/blog/ViewBlogCategory';

type BlogCategoryParams = {
  slug: string;
};

type BlogCategoryQuery = BlogCategoryParams & {
  page: string;
};

export type PageBlogCategoryProps = InferGetServerSidePropsType<typeof getServerSideProps>;

const PageBlogCategories = (pageProps: PageBlogCategoryProps) => {
  return useCMSPage({
    ...pageProps,
    ViewPage: ViewBlogCategory,
  });
};

const getServerSideProps = makeServerSideProps<
  Required<Omit<DynamicSEOEntity, 'metas'>> & {
    cmsResult: SanityBlogCategory;
    pageId: string;
  },
  BlogCategoryParams,
  BlogCategoryQuery,
  ERouting.BLOG_CATEGORY
>(ERouting.BLOG_CATEGORY, async (ctx) => {
  const res = await fetchSanityData<SanityBlogCategory>(ctx);
  if (isRedirect(res)) {
    return res;
  }
  const cmsResult = res.props;

  ctx.prefetches.push(
    getSanityQuery('blogCategoryArticlesQuery', {
      page: ctx.props.query.page,
      perPage: ARTICLES_TO_SHOW,
      categoryId: cmsResult._id,
    }),
  );

  const breadcrumb: BreadcrumbEntity = {
    breadcrumbType: ERoutingDynamic.BLOG_CATEGORY,
    index: 1,
    slug: cmsResult.pageSlug.slug.current,
    title: cmsResult.title,
  };

  return {
    props: {
      cmsResult: cmsResult,
      pageId: cmsResult._id,

      alternates: formatAlternates(cmsResult.alternates?.alt, ctx.keyRoute),
      breadcrumbs: [breadcrumb],
      blogCategories: cmsResult.blogCategories,
    },
  };
});

export { getServerSideProps };

export default PageBlogCategories;
